import React, { useState, useContext, useEffect } from 'react';
import SchedulingContext from '../Contexts/SchedulingContext';
import { SuperCalendar } from '@superops/ui-design-system';
import moment from 'moment';
import 'moment/locale/en-gb';
import { momentLocalizer } from 'react-big-calendar';
import {
  ALL_MODULES,
  EventEmitter,
  getCurrentUserHook,
  getDateTimeFormat,
  toast,
  useActivityTracker
} from '@nile/components';
import {
  GET_CALENDAR_VIEW_EVENTS,
  MockTicketClient,
  TicketClient,
  useCachedQuery,
  CREATE_APPOINTMENT
} from '@nile/graphql';
import { getDayDetails, getFormattedStartAndEndTime } from '../helpers/index';
import { useMutation, useQuery } from '@apollo/client';
import EventComponent from './EventComponent';
import EventWrapper from './EventWrapper';
import { cloneDeep, isEmpty } from 'lodash';
import dayjs from 'dayjs';
import CalendarHeader from './CalendarHeaders/CalendarHeader';
import { CALENDAR_VIEWS, scrollToCurrentTime } from '../helpers';
import { useGlobalState } from '@nile/components/src/Managers/StateManager';

let broadcastChannel;
if (BroadcastChannel) {
  broadcastChannel = new BroadcastChannel('SuperShare');
}

const sendWSEvent = (event, userId) => {
  const postMessageData = {
    context: 'pageUpdate',
    payload: {
      type: 'CLIENT_EVENT',
      page: {
        type: 'DETAIL',
        entity: 'UserCalendarInfo',
        recordId: userId
      },
      event: event,
      user: {
        userId: userId
      },
      epochMillis: new Date().getTime().toString()
    }
  };

  broadcastChannel.postMessage(
    JSON.stringify({
      name: 'wsdata',
      data: postMessageData
    })
  );
};

function getWeekOfYear(year, week) {
  // The first monday of the first week includes at least one day of the year
  let firstMondayOfYear = dayjs()
    .year(year)
    .isoWeek(week)
    .day(1);
  // Now make sure it really is the first monday of the year
  if (firstMondayOfYear.year() !== year) {
    firstMondayOfYear = firstMondayOfYear.add(7, 'days');
  }
  return new Array(7)
    .fill(firstMondayOfYear)
    .map((day, idx) => day.add(idx, 'day').format('YYYY-MM-DD'));
}

const CalendarContainer = () => {
  const schedulingContext = useContext(SchedulingContext);
  const currentUser = getCurrentUserHook();

  // Calendar 24 hours settings
  const is24hours = getDateTimeFormat()?.timeType === '24Hours';
  const mixPanelEventSource =
    schedulingContext?.mixPanelEventSource || ALL_MODULES.scheduling;

  if (is24hours) {
    moment.locale('en-GB');
  } else {
    moment.locale('ko', {
      week: {
        dow: 1,
        doy: 1
      }
    });
  }

  const localizer = momentLocalizer(moment); // End calendar settings

  const [events, setEvents] = useState([]);
  const [slaEvents, setSlaEvents] = useState([]);

  const getStartAndEndTime = () => {
    if (schedulingContext?.calendarView === 'day') {
      const selectedDate = schedulingContext?.selectedDate;
      const dayDetails = getDayDetails(selectedDate);
      return {
        startTime: dayDetails?.startTime,
        endTime: dayDetails?.endTime
      };
    }
    if (schedulingContext?.calendarView === 'week') {
      const currentWeekRange = getWeekOfYear(
        dayjs(new Date()).year(),
        dayjs(dayjs(new Date()).format('YYYY-MM-DD')).isoWeek()
      );
      const selectedWeekRange =
        schedulingContext?.selectedWeekRange || currentWeekRange;
      const startDay = selectedWeekRange?.[0];
      const endDay = selectedWeekRange?.[6];
      const startDayDetails = getDayDetails(startDay);
      const endDayDetails = getDayDetails(endDay);
      return {
        startTime: startDayDetails?.startTime,
        endTime: endDayDetails?.endTime
      };
    }
  };

  const getCalendarVariables = () => {
    const { startTime, endTime } = getStartAndEndTime();

    const SLA_CONDITION_INPUT = {
      joinOperator: 'AND',
      operands: [
        {
          attribute: 'eventType',
          operator: 'is',
          value: 'SLA_EVENT'
        },
        {
          attribute: 'workItem.ticket.technician.userId',
          operator: 'is',
          value: currentUser?.userId
        }
      ]
    };

    const filterConditions = schedulingContext?.filterConditions;

    const firstOperatorCondition = [
      {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'eventType',
            operator: 'is',
            value: 'APPOINTMENT'
          },
          {
            attribute: 'appointment.attendees.user.userId',
            operator: 'is',
            value: currentUser?.userId
          }
        ]
      }
    ];

    if (schedulingContext?.showSla) {
      firstOperatorCondition.push(SLA_CONDITION_INPUT);
    }

    const mainConditions = [
      {
        joinOperator: 'OR',
        operands: [
          {
            attribute: 'startTime',
            operator: 'between',
            value: [startTime, endTime]
          },
          {
            attribute: 'endTime',
            operator: 'between',
            value: [startTime, endTime]
          },
          {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'startTime',
                operator: 'before',
                value: startTime
              },
              {
                attribute: 'endTime',
                operator: 'after',
                value: endTime
              }
            ]
          }
        ]
      }
    ];

    if (filterConditions) {
      mainConditions.push(filterConditions);
    }

    if (schedulingContext?.calendarView === CALENDAR_VIEWS.DAY) {
      return {
        input: {
          condition: {
            joinOperator: 'AND',
            operands: [
              {
                joinOperator: 'OR',
                operands: firstOperatorCondition
              },
              ...mainConditions
            ]
          }
        }
      };
    } else {
      return {
        input: {
          groupBy: {
            attribute: 'startTime',
            unit: 'DAY'
          },
          condition: {
            joinOperator: 'AND',
            operands: [
              {
                joinOperator: 'OR',
                operands: firstOperatorCondition
              },
              ...mainConditions
            ]
          }
        }
      };
    }
  };

  const onCancelNewEvent = () => {
    const updatedEvents = cloneDeep(events).filter(
      (el) => el?.eventType !== 'NEW_EVENT'
    );
    setEvents(updatedEvents);
  };

  const getIsBeforeIntegratedDate = (startDate) => {
    const integratedDate = dayjs(schedulingContext?.integratedDate);
    const start = dayjs(startDate);

    return start.isBefore(integratedDate);
  };

  const getMixPanelEvent = (module) => {
    switch (module) {
      case ALL_MODULES.ticket:
        return 'Clicked schedule event from ticket';
      case ALL_MODULES.project:
        return 'Clicked schedule event from project';
      case ALL_MODULES.scheduling:
        return 'Clicked schedule event from my calendar';
      default:
        return '';
    }
  };
  const addNewEvent = (date) => {
    const isBeforeIntegratedDate = getIsBeforeIntegratedDate(date?.start);
    if (!isBeforeIntegratedDate) {
      const prevEvents = cloneDeep(events).filter(
        (el) => el?.eventType !== 'NEW_EVENT'
      );
      const user = {
        ...currentUser,
        label: currentUser?.name,
        value: currentUser?.name,
        id: currentUser?.userId,
        attendeeType: 'ORGANIZER',
        acceptanceStatus: 'ORGANIZER'
      };
      const updatedEvents = [
        ...prevEvents,
        {
          start: date?.start,
          end: date?.end,
          eventType: 'NEW_EVENT',
          onCancel: onCancelNewEvent,
          events: events,
          setEvents: setEvents,
          attendees: [user]
        }
      ];

      const mixpanelEvent = getMixPanelEvent(mixPanelEventSource);

      if (mixpanelEvent) {
        useActivityTracker({
          event: mixpanelEvent
        });
      }

      setEvents(updatedEvents);
    } else {
      toast('warning', 'scheduling.appointment.create.time.warning');
    }
  };

  const {
    loading,
    data: calendarEvents,
    refetch: refetchCalendarView
  } = useQuery(GET_CALENDAR_VIEW_EVENTS, {
    client: TicketClient,
    variables: getCalendarVariables(),
    fetchPolicy: 'network-only',
    onCompleted() {
      EventEmitter.dispatch('refreshNavigationSchedulingWidget');
    }
  });

  broadcastChannel.onmessage = (event) => {
    const eventData = JSON.parse(event.data);
    const { context, payload } = eventData;
    if (
      payload?.type === 'SERVER_EVENT' &&
      payload?.page?.entity === 'CalendarEvent' &&
      payload?.page?.type === 'DETAIL' &&
      payload?.event === 'UPDATE'
    ) {
      const appointmentToSync = payload?.data?.appointment;
      if (appointmentToSync) {
        const updatedEvents = [];
        const allEvents = cloneDeep(events);
        allEvents.forEach((event) => {
          if (
            event?.appointment?.appointmentId ===
            appointmentToSync?.appointmentId
          ) {
            const isDeleted = appointmentToSync?.deleted === 'true';
            // If deleted is true we wont push it to the state
            if (!isDeleted) {
              const updatedAppointment = {
                ...event,
                appointment: {
                  ...event?.appointment,
                  syncStatus: appointmentToSync?.syncStatus
                }
              };
              updatedEvents.push(updatedAppointment);
            }
          } else {
            updatedEvents.push(event);
          }
        });
        EventEmitter.dispatch('refreshNavigationSchedulingWidget');
        setEvents(updatedEvents);
      }
    }
  };

  useEffect(() => {
    scrollToCurrentTime();
  }, [schedulingContext?.selectedDate, loading]);

  const view = schedulingContext?.calendarView;
  useEffect(() => {
    const allEvents = calendarEvents?.getCalendarView?.flatMap((el) => {
      const events = el?.events;
      const selectedDate =
        view === 'day'
          ? schedulingContext?.selectedDate
          : el?.header?.value?.date;
      return events.map((event) => ({ ...event, selectedDate }));
    });
    if (allEvents) {
      const formattedSlaEvents = [];
      const formattedEvents = [];
      allEvents.forEach((el) => {
        const { start, end } = getFormattedStartAndEndTime(
          el?.startTime,
          el?.endTime,
          el?.selectedDate
        );
        if (el?.eventType === 'SLA_EVENT') {
          formattedSlaEvents.push({
            start: start,
            end: end,
            ...el
          });
        } else {
          formattedEvents.push({
            start: start,
            end: end,
            ...el
          });
        }
      });

      setSlaEvents(formattedSlaEvents);
      setEvents(formattedEvents);
      EventEmitter.dispatch('refreshNavigationSchedulingWidget');
    }
  }, [calendarEvents]);

  const refreshCalendar = () => {
    refetchCalendarView().then((res) => {
      const events = res?.data?.getCalendarView?.flatMap((el) => {
        const events = el?.events;
        const selectedDate =
          view === 'day'
            ? schedulingContext?.selectedDate
            : el?.header?.value?.date;
        return events.map((event) => ({ ...event, selectedDate }));
      });

      if (events) {
        const formattedSlaEvents = [];
        const formattedEvents = [];
        events.forEach((el) => {
          const { start, end } = getFormattedStartAndEndTime(
            el?.startTime,
            el?.endTime,
            el?.selectedDate
          );
          if (el?.eventType === 'SLA_EVENT') {
            formattedSlaEvents.push({
              start: start,
              end: end,
              ...el
            });
          } else {
            formattedEvents.push({
              start: start,
              end: end,
              ...el
            });
          }
        });
        setSlaEvents(formattedSlaEvents);
        setEvents(formattedEvents);
      }
    });
  };

  useEffect(() => {
    EventEmitter.subscribe('refreshCalendar', refreshCalendar);
    sendWSEvent('REGISTER_PAGE', currentUser?.userId);
    return function cleanup() {
      EventEmitter.unsubscribe('refreshCalendar');
      sendWSEvent('EXIT_PAGE', currentUser?.userId);
    };
  }, []);

  return (
    <>
      <div
        className="fhp w100 scheduling-wrapper"
        id="calendar-container"
        key={JSON.stringify(schedulingContext)}
      >
        <SuperCalendar
          defaultView={schedulingContext?.calendarView}
          defaultDate={schedulingContext?.selectedDate}
          events={events}
          selectable
          localizer={localizer}
          components={{
            backgroundEvent: EventComponent,
            event: EventComponent,
            eventWrapper: EventWrapper,
            week: {
              header: CalendarHeader
            },
            day: {
              header: CalendarHeader
            }
          }}
          backgroundEvents={slaEvents}
          onSelectSlot={addNewEvent}
          allDayAccessor={'isAllDayEvent'}
        />
      </div>
    </>
  );
};

export default CalendarContainer;
